import { useMemo } from 'react';
import { get, isEmpty } from 'lodash';

/**
 * Safely extracts URL from data source configuration
 * @param {Object} dataSource - Data source configuration object
 * @returns {string} Data source URL or empty string
 */
export const getDataSourceUrl = (dataSource) => {
    return get(dataSource, 'url', '');
};

/**
 * Custom hook to prepare infrastructure list selector configuration
 * Provides memoized values with fallback defaults for safe component rendering
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.dataSource - Data source configuration with URL
 * @param {string} config.tableId - Table identifier
 * @param {string} config.emptyMessage - Empty state message
 * @param {string} config.fallbackTableId - Fallback table ID if main ID is empty
 * @param {string} config.defaultEmptyMessage - Default empty message if main message is empty
 * @returns {Object} Memoized configuration values
 */
export const useInfrastructureListConfig = ({
    dataSource,
    tableId,
    emptyMessage,
    fallbackTableId,
    defaultEmptyMessage,
}) => {
    const url = useMemo(() => {
        return getDataSourceUrl(dataSource);
    }, [dataSource]);

    const validTableId = useMemo(() => {
        return isEmpty(tableId) ? fallbackTableId : tableId;
    }, [tableId, fallbackTableId]);

    const validEmptyMessage = useMemo(() => {
        return isEmpty(emptyMessage) ? defaultEmptyMessage : emptyMessage;
    }, [emptyMessage, defaultEmptyMessage]);

    return {
        url,
        tableId: validTableId,
        emptyMessage: validEmptyMessage,
    };
};
